"use client"

import { useMemo, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { asArray } from "@/lib/as-array"
import {
  useGetBuckets,
  useGetContacts,
  useGetJobs,
  usePostBucketsContacts,
  usePostJobsJobIdPipelineAdd,
} from "openapi/api/endpoints"

/**
 * The two writes the record's overflow menu offers: put this candidate on
 * another job, or into a bucket.
 *
 * Both shipped in the pre-Graphite record and neither survived the rebuild —
 * the menu that replaced them had no handler at all. The lists load only when
 * the menu is open, because a record that is merely being read should not be
 * fetching every job and every bucket in the org.
 */

export type Choice = { id: string; label: string }

export function useCandidateRecordActions({
  candidateId,
  isOpen,
}: {
  candidateId: string
  isOpen: boolean
}) {
  const queryClient = useQueryClient()
  const enabled = Boolean(candidateId) && isOpen

  const { data: jobsResponse } = useGetJobs(undefined, { query: { enabled } })
  const { data: bucketsResponse } = useGetBuckets({ query: { enabled } })

  /*
    Buckets hold contacts, not candidates, so adding to one needs the contact
    record that stands for this person. A candidate with no contact yet cannot
    be bucketed, and the menu says so rather than failing on click.
  */
  const { data: contacts } = useGetContacts(
    { candidateId },
    { query: { enabled } },
  )
  const contactId = asArray<{ id?: string }>(contacts)[0]?.id

  const jobs = useMemo<Choice[]>(
    () =>
      asArray<Record<string, unknown>>(
        (jobsResponse as { jobs?: unknown })?.jobs ?? jobsResponse,
      )
        .filter((job) => job.id)
        .map((job) => ({
          id: job.id as string,
          label:
            [job.title, job.company_name].filter(Boolean).join(" · ") ||
            (job.id as string),
        })),
    [jobsResponse],
  )

  const buckets = useMemo<Choice[]>(
    () =>
      asArray<Record<string, unknown>>(bucketsResponse)
        .filter((bucket) => bucket.id)
        .map((bucket) => ({
          id: bucket.id as string,
          label: (bucket.name as string) ?? (bucket.id as string),
        })),
    [bucketsResponse],
  )

  const { mutateAsync: addToJob, isPending: isAddingToJob } =
    usePostJobsJobIdPipelineAdd()
  const { mutateAsync: addToBucket, isPending: isAddingToBucket } =
    usePostBucketsContacts()

  const [busy, setBusy] = useState(false)

  const putOnJob = async (jobId: string, label: string) => {
    setBusy(true)
    try {
      await addToJob({ jobId, data: { candidateIds: [candidateId], stage: "longlist" } })
      await queryClient.invalidateQueries({
        queryKey: [`/candidates/${candidateId}/jobs`],
        exact: false,
      })
      await queryClient.invalidateQueries({
        queryKey: [`/jobs/${jobId}/pipeline`],
        exact: false,
      })
      toast.success(`Added to ${label}`, { description: "They are on the longlist." })
    } catch (error) {
      toast.error("Could not add to that job", { description: `${error}` })
    } finally {
      setBusy(false)
    }
  }

  const putInBucket = async (bucketId: string, label: string) => {
    if (!contactId) {
      toast.error("No contact record", {
        description: "Buckets hold contacts, and this candidate does not have one yet.",
      })
      return
    }

    setBusy(true)
    try {
      await addToBucket({ data: { bucket_id: bucketId, contact_ids: [contactId] } })
      await queryClient.invalidateQueries({
        queryKey: [`/contacts/${contactId}/buckets`],
        exact: false,
      })
      toast.success(`Added to ${label}`)
    } catch (error) {
      toast.error("Could not add to that bucket", { description: `${error}` })
    } finally {
      setBusy(false)
    }
  }

  return {
    jobs,
    buckets,
    canBucket: Boolean(contactId),
    isBusy: busy || isAddingToJob || isAddingToBucket,
    putOnJob,
    putInBucket,
  }
}
